import type { DataOption } from '@/shared/components/HookForm/types'

import { useTranslation } from 'react-i18next'

import { useColumnsSchemaQuery } from '@/entities/column'

import { HiddenOnContext } from '@/shared/generated'

/**
 * Product columns visible in the pricing-tactics context, mapped to
 * `{ value: field_schema_id, label: name }` for the `price_column` param.
 * The id is what gets persisted — it doubles as the `pricing_line.<id>` path
 * the rule graph resolves at pricing time, so labels must never be stored.
 * `name` is a key in the `fields` namespace, not display text.
 */
export const usePriceColumnOptions = () => {
	const { t } = useTranslation()
	const query = useColumnsSchemaQuery<DataOption[]>(
		HiddenOnContext.PricingTactics,
		{
			select: (data) =>
				(data ?? [])
					.filter((f) => !!f.id)
					.map((f) => ({
						value: f.id,
						// Guard the empty name: t('') throws "key is required".
						label: f.name
							? t(f.name, { ns: 'fields', defaultValue: f.name })
							: f.id,
					})),
		}
	)

	return {
		options: query.data ?? [],
		isLoading: query.isLoading,
		isError: query.isError,
		refetch: query.refetch,
	}
}
